import { useMemo, useState } from 'react'
import { Avatar } from '../../components/ui/Avatar'
import { Input } from '../../components/ui/Input'
import type { ChatMessage } from '../../api/types'
import styles from './MessageBubble.module.css'

const QUICK_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀']

interface MessageBubbleProps {
  message: ChatMessage
  currentUserId: string
  canModerate: boolean
  onReply: (message: ChatMessage) => void
  onEdit: (message: ChatMessage, body: string) => void
  onDelete: (message: ChatMessage) => void
  onToggleReaction: (message: ChatMessage, emoji: string) => void
}

export function MessageBubble({ message, currentUserId, canModerate, onReply, onEdit, onDelete, onToggleReaction }: MessageBubbleProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(message.body)

  const groupedReactions = useMemo(() => {
    const groups = new Map<string, { emoji: string; count: number; mine: boolean }>()
    for (const r of message.reactions) {
      const entry = groups.get(r.emoji) ?? { emoji: r.emoji, count: 0, mine: false }
      entry.count += 1
      if (r.userId === currentUserId) entry.mine = true
      groups.set(r.emoji, entry)
    }
    return [...groups.values()]
  }, [message.reactions, currentUserId])

  const isOwn = message.authorId === currentUserId
  const isDeleted = Boolean(message.deletedAt)

  return (
    <div className={[styles.row, isOwn ? styles.rowOwn : ''].join(' ')}>
      {!isOwn ? <Avatar name={message.author.displayName} imageUrl={message.author.avatarUrl} /> : null}
      <div className={styles.bubbleWrap}>
        <div className={[styles.bubble, isOwn ? styles.bubbleOwn : styles.bubbleOther].join(' ')}>
          {message.replyTo ? (
            <div className={styles.replyPreview}>
              ↪ {message.replyTo.author.displayName}: {message.replyTo.body.slice(0, 80)}
            </div>
          ) : null}
          {!isOwn ? <div className={styles.author}>{message.author.displayName}</div> : null}

          {isDeleted ? (
            <div className={[styles.body, styles.deleted].join(' ')}>Nachricht gelöscht</div>
          ) : editing ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onEdit(message, draft)
                    setEditing(false)
                  }
                  if (e.key === 'Escape') setEditing(false)
                }}
                autoFocus
              />
            </div>
          ) : (
            <div className={styles.body}>{message.body}</div>
          )}

          <div className={styles.meta}>
            {message.editedAt ? <span>bearbeitet · </span> : null}
            {new Date(message.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          </div>

          {!isDeleted ? (
            <div className={styles.actions}>
              {QUICK_EMOJIS.slice(0, 3).map((emoji) => (
                <button key={emoji} className={styles.actionBtn} onClick={() => onToggleReaction(message, emoji)}>
                  {emoji}
                </button>
              ))}
              <button className={styles.actionBtn} onClick={() => onReply(message)}>
                ↪
              </button>
              {isOwn ? (
                <button className={styles.actionBtn} onClick={() => setEditing(true)}>
                  ✎
                </button>
              ) : null}
              {isOwn || canModerate ? (
                <button className={styles.actionBtn} onClick={() => onDelete(message)}>
                  🗑
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {!isDeleted && groupedReactions.length > 0 ? (
          <div className={[styles.reactions, isOwn ? styles.reactionsOwn : ''].join(' ')}>
            {groupedReactions.map((r) => (
              <button
                key={r.emoji}
                className={[styles.reactionChip, r.mine ? styles.reactionChipActive : ''].join(' ')}
                onClick={() => onToggleReaction(message, r.emoji)}
              >
                {r.emoji} {r.count}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
